import { useMemo } from 'react';
import type { AppState } from '../../app/types';
import { MODULES } from '../../content';
import { MODULE_MASTERED_THRESHOLD } from '../../config/constants';
import type { AchievementCheckData } from './achievementData';

/**
 * useAchievementCheckData — mascot/achievement-domain selector that assembles
 * the canonical `AchievementCheckData` from learner `AppState` + the two store
 * read-accessors.
 *
 * DDD note: this assembly (totalQuestionsAnswered + masteredCount tallies plus
 * the streak / daily-goal / lab-session field mapping) previously lived inline
 * as a `useMemo` in `CollectionPage` — and in near-identical form in
 * `ProgressPage`, `AchievementCeremony`, and `useAchievementCount`. Centralising
 * it here removes the duplication and makes the gate-input contract a single
 * named domain concern. `AchievementCheckData` is the SSOT base type that the
 * unlock layer (`UnlockContext`) extends, so this selector is the natural home
 * for "turn raw app state into the unlock-gate input".
 *
 * Pure selector: it takes the inputs it needs rather than calling `useAppStore`
 * itself, so any host (the collection page, the progress page, a future
 * always-mounted learner shell) can drive it with the same contract.
 */
export function useAchievementCheckData(state: AppState, getAnsweredQuestions: (moduleId: string) => unknown[], getModuleMasteryForGroup: (moduleId: string) => number): AchievementCheckData {
  const totalQuestionsAnswered = useMemo(() => MODULES.reduce((total, module) => total + getAnsweredQuestions(module.id).length, 0), [getAnsweredQuestions]);
  const masteredCount = useMemo(() => {
    let mastered = 0;
    MODULES.forEach(m => {
      if (getModuleMasteryForGroup(m.id) >= MODULE_MASTERED_THRESHOLD) {
        mastered++;
      }
    });
    return mastered;
  }, [getModuleMasteryForGroup]);
  const currentWeekDays = state.dailyStreak?.currentWeekDays || 0;
  const consecutiveFullWeeks = state.dailyStreak?.consecutiveFullWeeks || 0;
  const longestTotalStreak = state.streak?.longestTotalStreak || 0;
  const totalDailyGoalsReached = state.dailyStreak?.totalDailyGoalsReached ?? (state.dailyStreak?.weeklyHistory ? Object.values(state.dailyStreak.weeklyHistory).reduce((a, b) => a + b, 0) : 0);
  const totalPerfectSessions = state.dailyStreak?.totalPerfectSessions ?? 0;
  const labSessionsCompleted = state.stats.labSessionsCompleted ?? 0;
  return useMemo(() => ({
    totalQuestionsAnswered,
    masteredCount,
    level: state.stats.level,
    xp: state.stats.xp,
    currentWeekDays,
    consecutiveFullWeeks,
    longestTotalStreak,
    totalDailyGoalsReached,
    totalPerfectSessions,
    labSessionsCompleted
  }), [totalQuestionsAnswered, masteredCount, state.stats.level, state.stats.xp, currentWeekDays, consecutiveFullWeeks, longestTotalStreak, totalDailyGoalsReached, totalPerfectSessions, labSessionsCompleted]);
}